import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { useNoIndex } from "../../lib/usePageMeta";
import { CLARITY_PROJECT_ID } from "../../lib/clarity";

const ADMIN_ROLES = new Set([
  "super_admin", "admin", "compliance_admin", "verification_specialist",
  "catalog_manager", "order_manager", "content_editor",
  "support_agent", "read_only_auditor",
]);

const NAV = [
  { to: "/admin", label: "Overview", role: "any" },
  { to: "/admin/orders", label: "Orders", role: "any" },
  { to: "/admin/products", label: "Products", role: "any" },
  { to: "/admin/batches", label: "Batches / Lots", role: "any" },
  { to: "/admin/coa", label: "COA Documents", role: "any" },
  { to: "/admin/image-review", label: "Image Review", role: "any" },
  { to: "/admin/recalls", label: "Recalls", role: "compliance" },
  { to: "/admin/audit-logs", label: "Audit Logs", role: "any" },
  { to: "/admin/site-settings", label: "Site Settings", role: "super" },
  { to: "/admin/settings/shipping", label: "Shipping & Tax", role: "super" },
  { to: "/admin/wholesale-leads", label: "Wholesale Leads", role: "any" },
];

export const AdminGuard = ({ children }) => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  // Every admin route is noindex,nofollow — auth-gated back-office, not indexable content.
  useNoIndex(pathname);
  if (loading) return <div className="max-w-6xl mx-auto px-4 py-24 text-brand-muted">Loading…</div>;
  if (!user || !ADMIN_ROLES.has(user.role)) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-24 text-center" data-testid="admin-guard" data-clarity-mask="true">
        <div className="font-display text-2xl text-brand-navy">Administrator access required.</div>
        <Link to="/login" className="mt-6 inline-block text-brand-accent hover:text-brand-accent">Sign in</Link>
      </div>
    );
  }
  // Every admin route is fully masked from Microsoft Clarity — heatmaps
  // must never record customer PII, order data, or shipping details.
  return <div data-clarity-mask="true">{children}</div>;
};

export const AdminNav = () => {
  const { user } = useAuth();
  const filtered = NAV.filter((n) => {
    if (n.role === "super") return user?.role === "super_admin";
    return true;
  });
  const clarityUrl = `https://clarity.microsoft.com/projects/view/${CLARITY_PROJECT_ID}/dashboard`;
  return (
    <div className="border-b border-brand-border overflow-x-auto no-scrollbar">
      <div className="max-w-7xl mx-auto px-4 flex items-center gap-1">
        {filtered.map((n) => (
          <Link
            key={n.to}
            to={n.to}
            data-testid={`adminnav-${n.to.split("/").pop()}`}
            className="whitespace-nowrap px-4 py-3 text-xs font-mono uppercase tracking-widest text-brand-muted hover:text-brand-accent border-b-2 border-transparent hover:border-brand-accent/60 transition-colors"
          >
            {n.label}
          </Link>
        ))}
        {/*
          Admin-only external link to the Microsoft Clarity project
          dashboard (heatmaps + session recordings). Rendered as a
          plain anchor with `target="_blank" rel="noopener noreferrer"`
          — it is NOT a React Router route, so no public dashboard
          page is exposed on medicalpeptidestore.com.
        */}
        <a
          href={clarityUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="adminnav-behavior-analytics"
          className="whitespace-nowrap px-4 py-3 text-xs font-mono uppercase tracking-widest text-brand-muted hover:text-brand-accent border-b-2 border-transparent hover:border-brand-accent/60 transition-colors"
        >
          Behavior Analytics ↗
        </a>
      </div>
    </div>
  );
};
